import { Box, Stack, Switch, TextField, Typography } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { useCallback } from 'react';
import { StatChartStepOption, StatChartThresholdOptions } from '@/core';
import {
  ThresholdsEditor,
  ThresholdsEditorProps,
} from '@/plugin-editor-components/components/ThresholdsEditor/NewThresholdsEditor';
import { TextAndBackgroundColorPicker } from '@/plugin-editor-components/components/ThresholdsEditor/TextAndBackgroundColorPicker';

function ColorPicker<T extends Omit<StatChartStepOption, 'value'>>({
  step,
  onStepChange,
  defaultColor,
}: {
  step: T;
  onStepChange: (newStep: T) => void;
  defaultColor: string;
}) {
  const handleColorChange = useCallback(
    (color: { color?: string; background?: string }) => {
      onStepChange(
        produce(step, (draft) => {
          const newStep = { ...draft, color: color.color, background_color: color.background };

          if (newStep.color === undefined) {
            delete newStep['color'];
          }

          if (newStep.background_color === undefined) {
            delete newStep['background_color'];
          }

          return newStep;
        })
      );
    },
    [step, onStepChange]
  );

  return (
    <TextAndBackgroundColorPicker
      onColorChange={handleColorChange}
      color={{ color: step.color || defaultColor, background: step.background_color }}
    />
  );
}

export const StatChartThresholdsEditor = ({
  thresholds,
  onChange,
  hideThresholdMode,
}: {
  thresholds?: StatChartThresholdOptions;
  onChange: (newThresholds: StatChartThresholdOptions) => void;
  hideThresholdMode: boolean;
}) => {
  const props: ThresholdsEditorProps<StatChartStepOption, StatChartThresholdOptions> = {
    onChange,
    renderStepOptions: ({ step, onStepChange }) => {
      return <StatChartThresholdStepEditor step={step} onStepChange={onStepChange} />;
    },
    thresholds,
    hideThresholdMode,
  };

  return <ThresholdsEditor {...props} renderColorPicker={ColorPicker} />;
};

function StatChartThresholdStepEditor<T extends Omit<StatChartStepOption, 'value'>>({
  step,
  onStepChange,
}: {
  step: T;
  onStepChange: (newStep: T) => void;
}) {
  const handleMessageChange = useCallback(
    (newMessage: string) => {
      onStepChange(
        produce(step, (draft) => {
          return { ...draft, name: newMessage };
        })
      );
    },
    [step, onStepChange]
  );

  const handleOnlyShowMessageSwitch = useCallback(
    (onlyShowMessage: boolean) => {
      onStepChange(
        produce(step, (draft) => {
          draft.show_value = !onlyShowMessage;
        })
      );
    },
    [onStepChange, step]
  );

  return (
    <Stack>
      <TextField
        defaultValue={step.name || ''}
        label="Message"
        fullWidth
        onBlur={(event) => handleMessageChange(event.currentTarget.value)}
      />

      <Stack direction="row" alignItems="center">
        <Box flexGrow={1}>
          <Typography>Show only message</Typography>
        </Box>
        <Box flexGrow={0}>
          <Switch checked={step.show_value === false} onChange={(_, state) => handleOnlyShowMessageSwitch(state)} />
        </Box>
      </Stack>
    </Stack>
  );
}
